import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import type {
  NextPage,
  InferGetServerSidePropsType,
  GetServerSidePropsContext,
} from 'next';

import { flatten, pluralize } from 'sdk';
import { useGlobalState } from '~/state';
import { ActionType } from '~/state/types';
import { Title, Shelf } from 'types';
import { UserProfile } from 'types';
import { backendFunction } from '~/clients/firebase/client';
import Firebase from '@/clients/firebase/firebase-react-query';
import useShelves from '~/lib/hooks/use-shelves';
import useAuthenticatedAction from '~/lib/hooks/use-authenticated-action';

import SessionForm from '~/components/session-form';
import Spacer from '~/components/spacer';
import ThumbsUp from '~/assets/svg/icons/thumbs-up';

import TitleTile from '~/components/tiles/title';
import Button from '~/components/button';
import DropDown from '~/components/dropdown';
import CreateShelfModal from '~/components/shelves/create-shelf-modal';
import PublicModal from '~/components/shelves/public-modal';
import ShareModal from '~/components/shelves/share-modal';
import UserProfileImage from '~/components/user-profile-image';
import Callout from '~/components/callout';

import styles from '~/styles/pages/shelf.module.scss';
import COLORS from '../../../lib/helpers/color-helper';

import MoreIcon from '~/assets/svg/icons/more-horizontal';
import ShareSVG from '~/assets/svg/icons/share';
import Trash from '~/assets/svg/icons/trash';
import EditSVG from '~/assets/svg/icons/edit';
import LockSVG from '~/assets/svg/icons/lock';
import ShelfIcon from '~/assets/svg/icons/shelf-icon';
import EyeSVG from '~/assets/svg/icons/eye';
import Pill from '~/components/pill';
import useAnalytics from '../../../lib/hooks/use-analytics';
import { withUser } from '../../../lib/user';
import PageMetaData from '~/components/page-meta-data';

interface ShelfPageProps {
  shelf: Shelf;
  user: UserProfile;
  titles: Title[];
}

const ShelfPage: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = ({ shelfId, initialShelfData, username, isOwner }) => {
  const router = useRouter();
  const { state, dispatch } = useGlobalState();
  const { user: loggedInUser } = state;

  const [locallyLiked, setLocallyLiked] = useState<boolean>(null!);
  const [localLikesCount, setLocalLikesCount] = useState<number>(
    initialShelfData.data.shelf.likes || 0
  );


  const { handleLike: handleShelfLike } = useShelves();

  const [openDropdown, setOpenDropdown] = useState(false);

  const { analyticEvents } = useAnalytics();

  const { updateShelf } = useShelves();

  const {
    data: shelfPages,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = Firebase<ShelfPageProps>().backendFunctionWithPagination(
    'shelves-getShelf',
    { username, shelf_id: shelfId },
    undefined,
    {
      initialData: {
        pages: [initialShelfData],
      },
    }
  );

  const shelf = shelfPages?.pages[0]?.data?.shelf;
  const shelfUser = initialShelfData?.data?.user;

  const IS_LIKED = locallyLiked === null ? shelf.liked : locallyLiked;


  const titles = useMemo(() => {
    return shelfPages?.pages?.reduce((acc, page) => {
      if (!page?.data?.titles) return acc;
      return [...acc, ...page.data.titles];
    }, []);
  }, [shelfPages]);

  const IS_OWNER = useMemo(() => {
    if (isOwner === true || isOwner === false) return isOwner;
    if (!loggedInUser?.data?.uid) return undefined;
    return loggedInUser?.data?.username === username;
  }, [loggedInUser, username, isOwner]);

  // log a shelf view event
  const [shelfViewed, setShelfViewed] = useState<string>('');
  useEffect(() => {
    if (!shelf?.public || IS_OWNER || !analyticEvents.viewShelf) return;
    setShelfViewed((prev) => {
      if (prev === shelfId) return prev;
      analyticEvents.viewShelf({ shelf_id: shelfId as string, username: shelfUser?.username });
      return shelfId as string;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shelfId, IS_OWNER, shelfViewed, analyticEvents]);

  const handleShare = () => {
    dispatch({
      type: ActionType.ADD_MODAL_DATA,
      payload: {
        title: 'Share shelf',
        component: <ShareModal shelf={shelf} />,
      },
    });
  };

  const DropdownContainer = () => {
    const dropdownItems = [
      {
        label: 'Share',
        onClick: handleShare,
        icon: <ShareSVG fillColor={COLORS.copyDark} />,
      },
      {
        label: 'Edit details',
        onClick: () => {
          dispatch({
            type: ActionType.ADD_MODAL_DATA,
            payload: {
              title: 'Edit shelf details',
              component: (
                <CreateShelfModal
                  edit
                  initData={{
                    id: shelfId as string,
                    title: shelf.title,
                    description: shelf.description,
                    public: shelf.public,
                    notify: shelf.notify,
                  }}
                  callback={({ shelfId: _id }) => {
                    if (_id !== shelfId) {
                      router.push(`/shelves/${username}/${_id}`);
                    } else {
                      refetch();
                      //need to refetch the shelf data for public and notfy booleans,
                      //right now coming from server side props (from the initial data)
                    }
                  }}
                />
              ),
            },
          });
        },
        icon: <EditSVG />,
      },
      {
        label: 'Delete shelf',
        onClick: () => {
          dispatch({
            type: ActionType.ADD_MODAL_DATA,
            payload: {
              title: 'Delete shelf',
              message: `Are you sure you want to delete ${shelf.title}?`,
              component: (
                <div className={styles['delete-shelf-modal']}>
                  <Button
                    text='Cancel'
                    style='secondary'
                    onPress={() => {
                      dispatch({
                        type: ActionType.FLUSH_MODAL_DATA,
                      });
                    }}
                  />
                  <Button
                    text='Delete'
                    style='primary'
                    onPress={async () => {
                      await updateShelf({
                        op: 'delete',
                        id: shelfId as string,
                      });
                    }}
                  />
                </div>
              ),
            },
          });
        },
        icon: <Trash />,
      },
    ];

    return (
      <div className={styles['dropdown-container']}>
        {dropdownItems.map((item, index) => {
          return (
            <div
              key={index}
              className={styles['dropdown-container-item']}
              onClick={item.onClick}
            >
              <div className={styles['dropdown-container-icon']}>
                {item.icon}
              </div>
              <span className={styles['dropdown-container-label']}>
                {item.label}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  const handlePublicClick = () => {
    dispatch({
      type: ActionType.ADD_MODAL_DATA,
      payload: {
        component: <PublicModal shelf={shelf} callback={refetch} />,
      },
    });
  };

  const renderUser = () => {
    return (
      <div className={styles.user}>
        <div className={styles['user-image']}>
          <UserProfileImage user={shelfUser} link />
        </div>
        <div className={styles['user-info']}>
          <h4>{shelfUser?.name}</h4>
          <p>@{shelfUser?.username}</p>
        </div>
      </div>
    );
  };

  const renderMoreDropdown = () => {
    return (
      <div style={{ position: 'relative' }}>
        <Button
          icon={<MoreIcon height={24} width={24} />}
          size='small'
          style='clear'
          shape='circle'
          onPress={() => setOpenDropdown(true)}
        />
        <DropDown
          open={openDropdown}
          onHide={() => setOpenDropdown(false)}
          align='left'
        >
          <DropdownContainer />
        </DropDown>
      </div>
    );
  };

  const renderViews = () => {
    return (
      <div className={styles.views}>
        <EyeSVG fillColor={COLORS.copy} width={24} />
        <p className={styles['views-count']}>{shelf.views ? `${shelf.views}` : "0"}</p>
      </div>
    );
  };

  const { handleAuthAction } = useAuthenticatedAction({
    actionToCall: async () => {
      setLocalLikesCount((prev) => IS_LIKED ? prev - 1 : prev + 1);
      setLocallyLiked(!IS_LIKED);
      await handleShelfLike({ shelfId: shelf.id, userId: shelfUser.id });
    }
  });

  const handleLike = () => {
    handleAuthAction({
      title: 'Login To Like This Shelf',
      message:
        'You will need an account to like a shelf. Please login or create an account with one of the methods below.',
      component: <SessionForm />,
    });
  };

  function renderLikes() {
    return (
      <>
        <div className={styles['likes-container']}>
          <Pill
            text={`${localLikesCount}`}
            icon={<ThumbsUp fillColor={IS_LIKED ? COLORS.primary : COLORS.copy} />}
            onClick={handleLike}
            style='secondary'
          />
        </div>
      </>
    );

  }

  const renderStats = () => {
    return (
      <>
        <div className={styles.stats}>
          <Pill
            text={`${shelf.titlesCount ? shelf.titlesCount : '0'} ${pluralize('book', shelf.titlesCount)}`}
            style='secondary'
          />
          {renderViews()}
          {renderLikes()}
        </div>
        <Spacer />
        <div className={styles['stats-divider']} />
        <Spacer />
      </>

    );
  };

  const renderHeader = () => {
    return (
      <div className={styles.header}>
        <div className={styles['header-title']}>
          <h1>{shelf.title}</h1>
          {IS_OWNER ? renderMoreDropdown()
            :
            <div style={{ position: 'relative' }}>
              <Button
                icon={<ShareSVG fillColor={COLORS.copyDark} />}
                size='small'
                style='clear'
                shape='circle'
                onPress={handleShare}
              />
            </div>

          }
        </div>
        <h3 className={styles['header-description']}>{shelf.description}</h3>
      </div>
    );
  };

  const renderEmptyShelf = () => (
    <div className={styles['empty-state']}>
      <ShelfIcon width={250} height={250} />
      <h3>No books on this shelf</h3>
      {IS_OWNER ? (
        <p>
          Add books to your shelf and be notified if a book you wishlisted has
          dropped in price.
        </p>
      ) : (
        <p>{"This person hasn't added any books yet. Check back soon!"}</p>
      )}
    </div>
  );

  const renderOwnerShelf = () => (
    <p>
      This Shelf is{' '}
      <span
        className={styles['public-container-privacy']}
        onClick={handlePublicClick}
      >
        {shelf.public ? 'Public' : 'Private'}
        {!shelf.public && <LockSVG />}
      </span>
    </p>
  );

  const renderGrid = () => (
    <div className={styles['grid-container']}>
      <div className={styles.grid}>
        {titles.map((title: Title) => (
          <TitleTile
            key={title.id}
            title={title as Title}
            source={'shelf'}
            shelfData={{ shelf, refetch, isOwner: IS_OWNER }}
            sold={title?.selected_listing?.sold}
          />
        ))}
      </div>

      {hasNextPage && (
        <div className={styles['infinite-load-button']}>
          <Button
            text='Load More'
            disabled={isFetchingNextPage}
            onPress={() => {
              fetchNextPage();
            }}
          />
        </div>
      )}
    </div>
  );

  if ((IS_OWNER === false && !shelf.public) || !shelf) {
    return null;
  }

  return (
    <>
      <PageMetaData
        title={shelf.title + ' | ' + shelfUser?.name + ' | Pango Books'}
        description={
          shelf.description + ' | Pango Books' + ' | ' +
          'See all books including: ' + titles.slice(0, 12).map((title) => title?.title).join(', ')

        }
      />
      <div className={styles['content-container']}>
        {renderHeader()}
        {!IS_OWNER ? renderUser() : null}
        <div>
          <div className={styles['content-container']}>
            {renderStats()}

            <div className={styles['public-container']}>
              {IS_OWNER ? renderOwnerShelf() : null}
            </div>
            <Spacer />
            {titles?.length ? renderGrid() : renderEmptyShelf()}
          </div>
          {!isOwner &&
            <Callout
              title="Public Shelves from the Pango Community"
              message="Browse through shelves curated by the Pango community to find your next favorite book"
              style='tertiary'
              button={{
                text: 'Explore Shelves',
                link: {
                  href: '/shelves',
                },
              }}

            />
          }
        </div>
      </div>

    </>
  );
};

type ShelfProps = { username: string; shelf_id: string; };
type ShelfData = { data: ShelfPageProps; };

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { username, shelfId } = context.query;
  const authUser = await withUser(context);

  if (!username || !shelfId) {
    return {
      notFound: true,
    };
  }

  try {
    const shelfData = await backendFunction<ShelfData, ShelfProps>(
      'shelves-getShelf',
      {
        username: username as string,
        shelf_id: shelfId as string,
      }
    );

    const requestUid = authUser?.props?.user?.uid;
    const shelfOwnerUid = shelfData.data.user.id;
    const isOwner =
      requestUid && shelfOwnerUid ? requestUid === shelfOwnerUid : null;
    const isPublic = shelfData.data.shelf.public;

    if (isOwner === false && !isPublic) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        initialShelfData: shelfData,
        username: username,
        shelfId,
        isOwner,
      },
    };
  } catch (e: any) {
    return {
      notFound: true,
    };
  }
};

export default ShelfPage;
